// Pure 2D vector math in projected meters. No I/O, no geodesy — callers project to meters first
// (see projection.ts) so distances and angles here are Euclidean and exact at field scale.

export type Vec2 = [number, number];

export const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
export const add = (a: Vec2, b: Vec2): Vec2 => [a[0] + b[0], a[1] + b[1]];
export const scale = (a: Vec2, s: number): Vec2 => [a[0] * s, a[1] * s];
export const dot = (a: Vec2, b: Vec2): number => a[0] * b[0] + a[1] * b[1];
export const len = (a: Vec2): number => Math.hypot(a[0], a[1]);

export function normalize(a: Vec2): Vec2 {
  const l = len(a);
  return l === 0 ? [0, 0] : [a[0] / l, a[1] / l];
}

/** Unit vector rotated 90° to the LEFT of `u` (counter-clockwise). */
export const perpLeft = (u: Vec2): Vec2 => [-u[1], u[0]];
/** Unit vector rotated 90° to the RIGHT of `u` (clockwise). Positive lane offset direction. */
export const perpRight = (u: Vec2): Vec2 => [u[1], -u[0]];

export interface NearestResult {
  /** Closest point on the polyline, meters. */
  point: Vec2;
  /** Index of the segment [i, i+1] the closest point lies on. */
  segIndex: number;
  /** Distance from the query point, meters. */
  dist: number;
  /** Unit tangent (direction) of the segment the closest point lies on. */
  tangent: Vec2;
}

function nearestOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number } {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 === 0) return { point: a, t: 0 };
  let t = dot(sub(p, a), ab) / l2;
  t = Math.max(0, Math.min(1, t));
  return { point: [a[0] + ab[0] * t, a[1] + ab[1] * t], t };
}

/** Closest point on a polyline (>=2 vertices) to `p`, with the local tangent. */
export function nearestOnPolyline(p: Vec2, pts: readonly Vec2[]): NearestResult {
  let best: NearestResult = {
    point: pts[0] ?? [0, 0],
    segIndex: 0,
    dist: Infinity,
    tangent: [1, 0],
  };
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const { point } = nearestOnSegment(p, a, b);
    const d = len(sub(p, point));
    if (d < best.dist) {
      best = { point, segIndex: i, dist: d, tangent: normalize(sub(b, a)) };
    }
  }
  return best;
}

/**
 * Signed perpendicular offset of point `p` from the directed line through `a` with unit direction
 * `u`, in meters. Convention: positive = `p` is to the RIGHT of the direction of travel, negative =
 * to the LEFT. (Right = clockwise from `u`.) This matches the cross-track sign used for guidance.
 */
export function signedOffset(p: Vec2, a: Vec2, u: Vec2): number {
  const r = sub(p, a);
  // z-component of (u × r): positive when r is to the LEFT of u. Negate so RIGHT is positive.
  return -(u[0] * r[1] - u[1] * r[0]);
}

/** Normalize an angle in degrees to [-180, 180). */
export function normalizeDeg(deg: number): number {
  let d = ((deg + 180) % 360) - 180;
  if (d < -180) d += 360;
  return d;
}
